import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import api from '../services/api';
import useAuthStore, { AppUser } from '../stores/authStore';
import usePermissionsStore, { PermissionsMap } from '../stores/permissionsStore';
import useTenantStore, { TenantSettings } from '../stores/tenantStore';

/**
 * Bootstraps the auth session.
 * Must be mounted once at the top of the app (inside App.tsx).
 * Listens to Firebase auth state and calls /auth/verify to populate stores.
 */
export function useAuthBootstrap(): void {
  const { setUser, setLoading, logout } = useAuthStore();
  const { setPermissions, clearPermissions } = usePermissionsStore();
  const { setTenant } = useTenantStore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        logout();
        clearPermissions();
        return;
      }

      setLoading(true);
      try {
        const response = await api.post<{
          user: AppUser;
          permissions: PermissionsMap;
          tenant: TenantSettings;
        }>('/api/v1/auth/verify');

        setUser(response.data.user);
        setPermissions(response.data.permissions);
        setTenant(response.data.tenant);
      } catch (err) {
        // Token valid but user not in Firestore, backend error, or network error.
        // Log to console so devtools shows the real cause instead of silent signOut.
        console.error('[useAuthBootstrap] /auth/verify failed:', err);
        await auth.signOut();
        logout();
        clearPermissions();
      }
    });

    return unsubscribe;
  }, [setUser, setLoading, logout, setPermissions, clearPermissions, setTenant]);
}

export function useAuth() {
  return useAuthStore();
}
